import { useEffect, useRef } from 'react';
import * as THREE from 'three';
import { useShallow } from 'zustand/shallow';
import { useEditorStore, useFlareStore } from '../../../stores';
import useTrimlineStore from '../../../stores/useTrimlineStore';
import { CreateFlarePreviewMaterial } from '../../../utils/PreviewMaterialsHandler';
import { useToolManager } from '../../../hooks/useToolManager';
import { TOOLS_CONFIG } from '../../../constants/tools';
import { applyFlareToGeometry, hasFlareApplied } from './flareHelpers';
import { detectBoundary, getBoundaryPositions, computeBoundaryCentroid } from './boundaryDetection';
import { computeDistanceFromBoundary } from './distanceField';
import {
  calculateSegmentInfluences,
  createSegmentOverlay,
  updateOverlayVisuals,
  updateOverlayInfluences,
  updateOverlayDistances,
  disposeAllOverlays,
  createSegmentColoredOverlay,
  updateSegmentColoredOverlay,
} from './flareOverlayHelpers';
import {
  createFlareDebugVisualization,
  disposeDebugVisualization,
} from './flareDebugHelpers';
import FlaresSmootheningHandler from './FlaresSmootheningHandler';

const FlaresPreviewHandler = () => {
  // Store connections
  const [scene, shadedMesh, replaceShadedMesh, geometryVersion] = useEditorStore(
    useShallow((s) => [
      s.sceneRefs.scene,
      s.sceneRefs.shadedMesh,
      s.replaceShadedMesh,
      s.geometryVersion,
    ])
  );

  const flareState = useFlareStore(
    useShallow((s) => ({
      enabled: s.enabled,
      flareValues: s.flareValues,
      segmentHeightValues: s.segmentHeightValues,
      segmentDeflectValues: s.segmentDeflectValues,
      segmentFalloffHeightValues: s.segmentFalloffHeightValues,
      segmentFalloffDeflectValues: s.segmentFalloffDeflectValues,
      segmentNames: s.segmentNames,
      selectedSegmentIndex: s.selectedSegmentIndex,
      deflectBlendRatio: s.deflectBlendRatio,
    }))
  );

  const [previewMesh, setPreviewMesh, isFlareApplied, setFlareApplied, setBaseGeometry] = useFlareStore(
    useShallow((s) => [s.previewMesh, s.setPreviewMesh, s.isFlareApplied, s.setFlareApplied, s.setBaseGeometry])
  );

  const trimlineState = useTrimlineStore((s) => s.trimline);

  const { run } = useToolManager();

  // Refs for preview management
  const previewMeshRef = useRef(null);
  const baseGeomRef = useRef(null);
  const basePosRef = useRef(null);
  const lastMeshRef = useRef(null);
  const segmentMapRef = useRef(null);

  // Overlay system refs
  const overlayMeshRef = useRef(null); // NEW: Single colored overlay mesh (replaces multiple overlays)
  const lastGeometryRef = useRef(null); // Track geometry changes for overlay invalidation
  const distancesRef = useRef(null); // Distance field for gradient overlay influences
  const centroidRef = useRef(null); // World-space centroid for angular clipping

  // Debug visualization refs
  const debugLinesRef = useRef([]); // Array of Line2 objects for debug visualization

  // Track last seen geometry version for cache invalidation
  const lastGeometryVersionRef = useRef(geometryVersion);

  /**
   * Invalidate all cached geometry data
   * Called when geometry is modified by other tools (e.g., axes alignment)
   */
  function invalidateGeometryCache() {
    console.log('🔄 FlaresHandler: Invalidating geometry cache due to external geometry modification');

    // Clear all geometry-dependent caches
    baseGeomRef.current = null;
    basePosRef.current = null;
    lastMeshRef.current = null;
    segmentMapRef.current = null;
    distancesRef.current = null;
    centroidRef.current = null;
    lastGeometryRef.current = null;

    // Dispose preview and overlays to force recreation with new geometry
    disposePreview();
    disposeSegmentOverlay();
  }

  /**
   * Watch for geometry version changes from other tools
   * When geometry is modified in-place (e.g., by axes alignment), invalidate caches
   */
  useEffect(() => {
    if (geometryVersion !== lastGeometryVersionRef.current) {
      console.log('🔄 FlaresHandler: Geometry version changed from', lastGeometryVersionRef.current, 'to', geometryVersion);
      lastGeometryVersionRef.current = geometryVersion;

      // Only invalidate if we have cached data (skip on initial mount)
      if (baseGeomRef.current || previewMesh) {
        invalidateGeometryCache();

        // Recreate preview with fresh geometry if flares are enabled
        if (scene && shadedMesh && !isFlareApplied) {
          ensurePreview();
          rebuildPreview();
        }
      }
    }
  }, [geometryVersion]);

  /**
   * Create or get the preview mesh
   */
  function ensurePreview() {
    // Don't create preview if flare is already applied
    if (isFlareApplied) return null;

    if (previewMesh && previewMesh.parent) return previewMesh;
    if (previewMeshRef.current) return previewMeshRef.current;
    if (!scene || !shadedMesh) return null;

    // Create base geometry snapshot if needed
    if (!baseGeomRef.current || lastMeshRef.current !== shadedMesh) {
      baseGeomRef.current = shadedMesh.geometry.clone();
      if (!baseGeomRef.current.attributes.position) return null;

      basePosRef.current = baseGeomRef.current.attributes.position.array.slice();

      // Ensure normals exist
      if (!baseGeomRef.current.attributes.normal) {
        baseGeomRef.current.computeVertexNormals();
      }

      // Store base geometry in flare store for color restoration during apply
      setBaseGeometry(baseGeomRef.current);

      lastMeshRef.current = shadedMesh;
    }

    // Create preview mesh
    const cloneGeom = baseGeomRef.current.clone();
    const mat = CreateFlarePreviewMaterial(0.8);
    const clone = new THREE.Mesh(cloneGeom, mat);
    clone.name = 'FlarePreview';
    clone.matrixAutoUpdate = true;
    clone.position.copy(shadedMesh.position);
    clone.quaternion.copy(shadedMesh.quaternion);
    clone.scale.copy(shadedMesh.scale);
    scene.add(clone);

    previewMeshRef.current = clone;
    setPreviewMesh(clone);
    return clone;
  }

  /**
   * Dispose segment-colored overlay mesh
   */
  function disposeSegmentOverlay() {
    const overlay = overlayMeshRef.current;
    if (!overlay) return;

    if (overlay.parent) overlay.parent.remove(overlay);
    overlay.geometry?.dispose?.();
    overlay.material?.dispose?.();
    overlayMeshRef.current = null;
  }

  /**
   * Dispose preview mesh
   */
  function disposePreview(options = { keepIfReplaced: false }) {
    const p = previewMeshRef.current;
    if (!p) return;

    if (options.keepIfReplaced) {
      // Preview is being turned into the shaded mesh; don't dispose
      setPreviewMesh(null);
      return;
    }

    if (p.parent) p.parent.remove(p);
    p.geometry?.dispose?.();
    p.material?.dispose?.();
    setPreviewMesh(null);
    setBaseGeometry(null);
    previewMeshRef.current = null;

    // Dispose segment overlay (new system replaces old overlay system)
    disposeSegmentOverlay();
    // Old disposeOverlays() call removed - using new segment-colored overlay instead

    // Dispose debug visualization
    if (debugLinesRef.current.length > 0 && scene) {
      disposeDebugVisualization(debugLinesRef.current, scene);
    }
  }

  /**
   * Create or update segment-colored overlay mesh
   * NEW: Uses actual segment map from geometry for accurate visualization
   */
  function createOrUpdateSegmentOverlay() {
    const mesh = previewMesh || previewMeshRef.current;
    if (!mesh || !shadedMesh || isFlareApplied) return;

    const latestFlareState = useFlareStore.getState();

    // Get segment map from geometry userData (populated during flare application)
    const segmentMap = mesh.geometry.userData?.segmentMap;
    if (!segmentMap || segmentMap.size === 0) return;

    // Create or update the colored overlay
    if (!overlayMeshRef.current) {
      // Create new overlay
      const overlay = createSegmentColoredOverlay(
        mesh.geometry,
        segmentMap,
        latestFlareState.segmentNames,
        { selectedSegmentIndex: latestFlareState.selectedSegmentIndex }
      );
      mesh.add(overlay);
      overlayMeshRef.current = overlay;
    } else {
      // Update existing overlay colors
      updateSegmentColoredOverlay(
        overlayMeshRef.current,
        segmentMap,
        latestFlareState.segmentNames,
        latestFlareState.selectedSegmentIndex
      );
    }
  }

  /**
   * Ensure overlay meshes exist for all segments (OLD - kept for compatibility)
   */
  function ensureOverlays() {
    // Use the freshest preview mesh available; store state may lag within the same frame
    const mesh = previewMesh || previewMeshRef.current;
    if (!mesh || !shadedMesh || isFlareApplied) return;

    const latestFlareState = useFlareStore.getState();
    const segmentMap = segmentMapRef.current;

    if (!segmentMap || segmentMap.size === 0) return;

    // Check if geometry has changed (e.g., after alignment)
    // Use geometry UUID for more reliable detection than attribute reference
    const currentGeometryUUID = mesh.geometry.uuid;
    const geometryChanged = lastGeometryRef.current && lastGeometryRef.current !== currentGeometryUUID;

    if (geometryChanged) {
      // Invalidate all overlays when geometry changes
      console.log('🔄 FlaresHandler: Geometry UUID changed, disposing overlays');
      disposeOverlays();
    }

    lastGeometryRef.current = currentGeometryUUID;

    // Get overlay config
    const overlayConfig = latestFlareState.overlayConfig || {
      surfaceOffset: 0.0006,
      selectedBrightness: 1.2,
      unselectedBrightness: 0.7,
      edgeSmoothness: [0.45, 0.55],
      alphaMode: 'fill',
      angularBlendWidth: 6.0  // NEW: smooth blend over 6% of angular range at boundaries (wider for visible smoothing)
    };

    // Segment colors (matches FlareToolCard.jsx)
    const SEGMENT_COLORS = {
      purple: new THREE.Color('#B984D9'),
      yellow: new THREE.Color('#FFD152'),
      orange: new THREE.Color('#F19B4A'),
      blue: new THREE.Color('#3B9FF5'),
      violet: new THREE.Color('#9093DF')
    };

    // Calculate influences for all segments with gradient falloff
    const segmentCount = latestFlareState.segmentNames.length;
    const distances = distancesRef.current;
    // Convert centroid (currently in world) to the preview mesh local space for angular clipping
    const centroidLocal = centroidRef.current ? mesh.worldToLocal(centroidRef.current.clone()) : null;
    const influences = calculateSegmentInfluences(
      mesh.geometry,
      segmentMap,
      distances,
      latestFlareState.segmentHeightValues,
      segmentCount,
      latestFlareState.segmentFalloffHeightValues  // Pass falloff heights
    );

    // Create or update overlays for each segment
    for (let segIdx = 0; segIdx < segmentCount; segIdx++) {
      const segmentInfluence = influences.get(segIdx);
      if (!segmentInfluence) continue;

      // Check if any vertex has influence in this segment
      const hasInfluence = segmentInfluence.some(val => val > 0);
      if (!hasInfluence) continue;

      const segmentName = latestFlareState.segmentNames[segIdx];
      const color = SEGMENT_COLORS[segmentName] || new THREE.Color(0x808080);

      let overlay = overlayMeshesRef.current.get(segIdx);

      // Compute angular start/end for this segment (percent 0..100)
      const flareValues = latestFlareState.flareValues;
      const n = flareValues.length;
      const startPercent = segIdx === 0 ? flareValues[n - 1] : flareValues[segIdx - 1];
      const endPercent = flareValues[segIdx];

      if (!overlay) {
        // Create new overlay
        overlay = createSegmentOverlay(
          mesh.geometry,
          segIdx,
          segmentInfluence,
          color,
          overlayConfig,
          distances,  // Pass distance field for per-fragment calculation
          latestFlareState.segmentHeightValues[segIdx],  // Pass segment height
          centroidLocal, // centroid for angular clipping (local space)
          startPercent,
          endPercent,
          latestFlareState.segmentFalloffHeightValues?.[segIdx] || 0  // Pass falloff height
        );

        // Add to preview mesh (parent to preview so it moves with it)
        mesh.add(overlay);
        overlayMeshesRef.current.set(segIdx, overlay);
      } else {
        // Update existing overlay influences and distances
        updateOverlayInfluences(overlay, segmentInfluence);
        updateOverlayDistances(overlay, distances);
      }

      // Update visuals based on selection
      const isSelected = segIdx === latestFlareState.selectedSegmentIndex;
      updateOverlayVisuals(
        overlay,
        color,
        isSelected,
        overlayConfig,
        latestFlareState.segmentHeightValues[segIdx],  // Pass segment height for uniform update
        centroidLocal,
        startPercent,
        endPercent,
        latestFlareState.segmentFalloffHeightValues?.[segIdx] || 0  // Pass falloff height
      );
    }
  }

  /**
   * Dispose all overlay meshes
   */
  function disposeOverlays() {
    disposeAllOverlays(overlayMeshesRef.current);
    overlayMeshesRef.current.clear();
    lastGeometryRef.current = null;
  }

  /**
   * Create debug visualization of deflected boundary vertices
   */
  function rebuildDebugVisualization() {
    if (!scene || !shadedMesh) return;

    // Dispose existing debug lines
    if (debugLinesRef.current.length > 0) {
      disposeDebugVisualization(debugLinesRef.current, scene);
    }

    const latestFlareState = useFlareStore.getState();
    const trimlineState = useTrimlineStore.getState().trimline;

    // Create debug visualization using the actual flare algorithm
    const debugLines = createFlareDebugVisualization(
      shadedMesh,
      trimlineState,
      latestFlareState,
      scene
    );

    debugLinesRef.current = debugLines;
  }

  /**
   * Rebuild preview with current flare parameters (SYNCHRONOUS)
   */
  function rebuildPreview() {
    const p = ensurePreview();
    if (!p || !shadedMesh) return;

    // Get fresh parameters from store
    const latestFlareState = useFlareStore.getState();

    // Reset to base positions
    const previewPos = p.geometry.attributes.position;
    const basePositions = basePosRef.current;

    for (let i = 0; i < previewPos.count; i++) {
      previewPos.setXYZ(
        i,
        basePositions[i * 3],
        basePositions[i * 3 + 1],
        basePositions[i * 3 + 2]
      );
    }

    // If all values are 0, skip flare application to avoid NaN errors
    if (!hasFlareApplied(latestFlareState)) {
      previewPos.needsUpdate = true;
      p.geometry.computeVertexNormals();
      p.geometry.computeBoundingSphere();

      // Clear segment mapping and overlays
      segmentMapRef.current = new Map();
      distancesRef.current = null;
      centroidRef.current = null;
      disposeOverlays();

      return;
    }

    // Detect boundary vertices (SYNCHRONOUS)
    const boundaryVertices = detectBoundary(shadedMesh, trimlineState);

    if (!boundaryVertices || boundaryVertices.length === 0) {
      return;
    }

    // Use dynamic passes based on max total height (including falloff)
    const totalHeights = latestFlareState.segmentHeightValues.map((h, i) => {
      const falloffH = latestFlareState.segmentFalloffHeightValues?.[i] || 0;
      return h + falloffH;
    });
    const maxTotalHeightMm = Math.max(...totalHeights);
    const requiredPasses = Math.min(100, Math.max(15, Math.ceil(maxTotalHeightMm * 3)));

    // Compute distance field (SYNCHRONOUS)
    const distances = computeDistanceFromBoundary(
      p.geometry,
      boundaryVertices,
      requiredPasses
    );

    distancesRef.current = distances;

    // Compute world-space centroid for angular clipping
    if (boundaryVertices.length > 0) {
      const bPositions = getBoundaryPositions(shadedMesh, boundaryVertices);
      centroidRef.current = computeBoundaryCentroid(bPositions);
    } else {
      centroidRef.current = null;
    }

    // Apply flare effects (SYNCHRONOUS)
    const segmentMap = applyFlareToGeometry(
      p.geometry,
      shadedMesh,
      latestFlareState,
      trimlineState
    );

    segmentMapRef.current = segmentMap;

    previewPos.needsUpdate = true;
    p.geometry.computeVertexNormals();
    p.geometry.computeBoundingSphere();

    // NEW: Create/update segment-colored overlay using actual segment map
    // This ensures visualization matches the geometry deformation exactly
    if (segmentMap && segmentMap.size > 0) {
      createOrUpdateSegmentOverlay();
    }
  }

  // Cleanup and apply on unmount
  useEffect(() => {
    return () => {
      const latest = useFlareStore.getState();
      const hasFlare = hasFlareApplied(latest);

      if (latest.enabled && hasFlare && latest.previewMesh) {
        // Apply the flare transformation via tool manager
        run(
          { toolId: TOOLS_CONFIG.MAIN_PROCESS.FLARES.main_flares_flare.id, mode: 'apply' },
          TOOLS_CONFIG.MAIN_PROCESS.FLARES.main_flares_flare.id
        );

        // Clear base references (baseGeometry is cleared in createFlareCommand)
        baseGeomRef.current = null;
        basePosRef.current = null;
        lastMeshRef.current = null;
      } else {
        disposePreview();
        setPreviewMesh(null);
        setBaseGeometry(null);
      }
    };
  }, []);

  // Create preview when scene and mesh are ready
  useEffect(() => {
    if (!scene || !shadedMesh) return;

    if (isFlareApplied) {
      // Clear preview when applied
      disposePreview();
      return;
    }

    // Reset refs when switching back to preview mode (after unapply)
    if (!isFlareApplied) {
      // Clear all refs to ensure fresh snapshot on next preview creation
      baseGeomRef.current = null;
      basePosRef.current = null;
      lastMeshRef.current = null;
    }

    ensurePreview();
    rebuildPreview();
  }, [scene, shadedMesh, isFlareApplied]);

  // Rebuild preview when parameters change
  // Use stringified values to prevent spurious triggers from array reference changes
  const flareValuesStr = JSON.stringify(flareState.flareValues);
  const segmentHeightValuesStr = JSON.stringify(flareState.segmentHeightValues);
  const segmentDeflectValuesStr = JSON.stringify(flareState.segmentDeflectValues);
  const segmentFalloffHeightValuesStr = JSON.stringify(flareState.segmentFalloffHeightValues);
  const segmentFalloffDeflectValuesStr = JSON.stringify(flareState.segmentFalloffDeflectValues);

  useEffect(() => {
    // Skip if not ready or already applied (handled by scene/mesh useEffect above)
    if (!scene || !shadedMesh) return;
    if (isFlareApplied) return;

    // Debounce rapid parameter changes to batch multiple updates
    // When user changes multiple parameters quickly (e.g., height then deflect),
    // we wait for changes to settle before computing
    const debounceDelay = 50; // 50ms debounce
    const timeoutId = setTimeout(() => {
      rebuildPreview();
    }, debounceDelay);

    // Cancel pending rebuild if parameters change again
    return () => {
      clearTimeout(timeoutId);
    };
  }, [
    // NOTE: scene, shadedMesh, isFlareApplied are intentionally NOT included here
    // to avoid double-triggering with the useEffect above that handles initial setup
    flareValuesStr,
    segmentHeightValuesStr,
    segmentDeflectValuesStr,
    segmentFalloffHeightValuesStr,
    segmentFalloffDeflectValuesStr,
    flareState.deflectBlendRatio,
  ]);

  // Update overlay colors when selected segment changes (for highlighting)
  useEffect(() => {
    if (!previewMesh || !overlayMeshRef.current || isFlareApplied) return;

    const latestFlareState = useFlareStore.getState();
    const segmentMap = previewMesh.geometry.userData?.segmentMap;

    if (!segmentMap) return;

    // Update the segment-colored overlay to highlight selected segment
    updateSegmentColoredOverlay(
      overlayMeshRef.current,
      segmentMap,
      latestFlareState.segmentNames,
      latestFlareState.selectedSegmentIndex
    );
  }, [flareState.selectedSegmentIndex, previewMesh, isFlareApplied]);

  return null;
};

const FlaresHandler = () => {
  const { activeTool } = useToolManager();

  if (activeTool === TOOLS_CONFIG.MAIN_PROCESS.FLARES.main_flares_smoothning.id) {
    return <FlaresSmootheningHandler />;
  }

  return <FlaresPreviewHandler />;
};

export default FlaresHandler;
